import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  TablePagination,
  Button,
  Menu,
  Paper,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined';
import dayjs from 'dayjs';
import Papa from 'papaparse';
import ExcelJS from 'exceljs';
import { fleetApi } from '../../api/axiosClient';
import { useLanguage } from '../../utils/i18n';

// value is used for filter logic, label is translated for display
const filterOptions = [
  { value: 'All', key: 'general.all', fallback: 'All' },
  { value: 'IP', key: 'userActivity.ip', fallback: 'IP' },
  { value: 'Company', key: 'general.company', fallback: 'Company' },
  { value: 'Username', key: 'general.username', fallback: 'Username' },
  { value: 'Role', key: 'general.role', fallback: 'Role' },
  { value: 'Duration', key: 'userActivity.duration', fallback: 'Duration' },
  { value: 'Login Count', key: 'userActivity.loginCount', fallback: 'Login Count' },
  { value: 'Access Endpoint Count', key: 'userActivity.accessEndpointCount', fallback: 'Access Endpoint Count' },
  { value: 'User Agent', key: 'userActivity.userAgent', fallback: 'User Agent' },
  { value: 'Last Login', key: 'userActivity.lastLogin', fallback: 'Last Login' },
  { value: 'Last Access Endpoint', key: 'userActivity.lastAccessEndpoint', fallback: 'Last Access Endpoint' }
];

// company can be an object from API; always use text to avoid "Objects are not valid as a React child"
const toText = (v) => (v && typeof v === 'object' ? v.name || v.label || v.company_name || '' : (v ?? ''));

const headerStyle = {
  backgroundColor: '#5166AE',
  color: '#ffffff',
  whiteSpace: 'nowrap',
  fontWeight: 600,
  lineHeight: '1.1',
  border: 'none',
  padding: '12px 16px',
  textAlign: 'left'
};

const getStickyStyle = (leftPos, z, bgColor) => ({
  position: 'sticky',
  left: leftPos,
  zIndex: z,
  backgroundColor: bgColor
});

const cellStyle = {
  border: 'none',
  borderBottom: '1px solid rgba(224, 224, 224, 1)',
  padding: '12px 16px',
  textAlign: 'left'
};

export default function UserActivityLogPage() {
  const { t } = useLanguage();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchDate, setSearchDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [filterType, setFilterType] = useState('All');
  const [filterValue, setFilterValue] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Export Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const openExport = Boolean(anchorEl);

  useEffect(() => {
    const fetchData = async () => {
      if (!searchDate) return;
      setLoading(true);
      try {
        const response = await fleetApi.get(`/trace-record?date=${searchDate}`);
        setData(response.data || []);
      } catch (error) {
        console.error('Failed to fetch activity logs:', error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchDate]);

  const filteredData = useMemo(() => {
    if (filterType === 'All' && !filterValue) return data;

    const lowerFilter = filterValue.toLowerCase();

    return data.filter((row) => {
      if (filterType === 'All') {
        return (
          row.ip?.toLowerCase().includes(lowerFilter) ||
          String(toText(row.company)).toLowerCase().includes(lowerFilter) ||
          row.username?.toLowerCase().includes(lowerFilter) ||
          row.role?.toLowerCase().includes(lowerFilter) ||
          row.durationHms?.toLowerCase().includes(lowerFilter) ||
          row.loginCount?.toString().includes(lowerFilter) ||
          row.accessEndpointCount?.toString().includes(lowerFilter) ||
          row.userAgents?.some((ua) => ua.toLowerCase().includes(lowerFilter))
        );
      }

      switch (filterType) {
        case 'IP':
          return row.ip?.toLowerCase().includes(lowerFilter);
        case 'Company':
          return String(toText(row.company)).toLowerCase().includes(lowerFilter);
        case 'Username':
          return row.username?.toLowerCase().includes(lowerFilter);
        case 'Role':
          return row.role?.toLowerCase().includes(lowerFilter);
        case 'Duration':
          return row.durationHms?.toLowerCase().includes(lowerFilter) || row.duration?.toString().includes(lowerFilter);
        case 'Login Count':
          return row.loginCount?.toString().includes(lowerFilter);
        case 'Access Endpoint Count':
          return row.accessEndpointCount?.toString().includes(lowerFilter);
        case 'User Agent':
          return row.userAgents?.some((ua) => ua.toLowerCase().includes(lowerFilter));
        case 'Last Login':
          return row.lastLoginAt ? dayjs(row.lastLoginAt).format('HH:mm').includes(filterValue) : false;
        case 'Last Access Endpoint':
          return row.lastAccessEndpointAt ? dayjs(row.lastAccessEndpointAt).format('HH:mm').includes(filterValue) : false;
        default:
          return true;
      }
    });
  }, [data, filterType, filterValue]);

  // Removed useEffect on filteredData.length to avoid infinite loops
  const totalPages = Math.max(0, Math.ceil(filteredData.length / rowsPerPage) - 1);
  const validPage = Math.min(page, totalPages);

  const handleFilterTypeChange = (e) => {
    setFilterType(e.target.value);
    setFilterValue('');
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = useMemo(() => {
    return filteredData.slice(validPage * rowsPerPage, validPage * rowsPerPage + rowsPerPage);
  }, [filteredData, validPage, rowsPerPage]);

  const isTimeFilter = filterType === 'Last Login' || filterType === 'Last Access Endpoint';

  const handleExportClick = (e) => setAnchorEl(e.currentTarget);
  const handleExportClose = () => setAnchorEl(null);

  const exportHeaders = () => ({
    ip: t('userActivity.ip', 'IP'),
    company: t('general.company', 'Company'),
    username: t('general.username', 'Username'),
    role: t('general.role', 'Role'),
    lastLogin: t('userActivity.lastLogin', 'Last Login'),
    lastAccess: t('userActivity.lastAccessEndpoint', 'Last Access Endpoint'),
    duration: t('userActivity.duration', 'Duration'),
    loginCount: t('userActivity.loginCount', 'Login Count'),
    accessCount: t('userActivity.accessEndpointCount', 'Access Endpoint Count'),
    userAgent: t('userActivity.userAgent', 'User Agent')
  });

  const buildExportRow = (row) => ({
    ip: row.ip,
    company: toText(row.company),
    username: row.username,
    role: row.role,
    lastLogin: row.lastLoginAt ? dayjs(row.lastLoginAt).format('DD MMM YYYY HH:mm:ss') : '-',
    lastAccess: row.lastAccessEndpointAt ? dayjs(row.lastAccessEndpointAt).format('DD MMM YYYY HH:mm:ss') : '-',
    duration: row.durationHms || row.duration,
    loginCount: row.loginCount,
    accessCount: row.accessEndpointCount,
    userAgent: row.userAgents ? row.userAgents.join(', ') : '-'
  });

  const exportToCSV = () => {
    const headers = exportHeaders();
    const formattedData = filteredData.map((row) => {
      const r = buildExportRow(row);
      return Object.fromEntries(Object.keys(headers).map((k) => [headers[k], r[k]]));
    });

    const csvData = Papa.unparse(formattedData);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `User-Activity-Logs-${dayjs(searchDate).format('YYYYMMDD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setAnchorEl(null);
  };

  const exportToExcel = async () => {
    const headers = exportHeaders();
    const workbook = new ExcelJS.Workbook();
    // Excel sheet names are limited to 31 chars
    const worksheet = workbook.addWorksheet(String(t('userActivity.logSheetName', 'User Activity Logs')).slice(0, 31));

    worksheet.columns = [
      { header: headers.ip, key: 'ip', width: 20 },
      { header: headers.company, key: 'company', width: 30 },
      { header: headers.username, key: 'username', width: 20 },
      { header: headers.role, key: 'role', width: 20 },
      { header: headers.lastLogin, key: 'lastLogin', width: 25 },
      { header: headers.lastAccess, key: 'lastAccess', width: 25 },
      { header: headers.duration, key: 'duration', width: 15 },
      { header: headers.loginCount, key: 'loginCount', width: 15 },
      { header: headers.accessCount, key: 'accessCount', width: 20 },
      { header: headers.userAgent, key: 'userAgent', width: 50 }
    ];

    filteredData.forEach((row) => {
      worksheet.addRow(buildExportRow(row));
    });

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF5166AE' } // Matches header color #5166AE
    };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    });

    worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `User-Activity-Logs-${dayjs(searchDate).format('YYYYMMDD')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setAnchorEl(null);
  };

  return (
    <Box sx={{ width: '100%', height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      <Paper elevation={0} sx={{ p: 2, mb: 2, flexShrink: 0 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {t('userActivity.logTitle', 'User Login Activity Logs')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'center' }}>
          <DatePicker
            label={t('userActivity.searchDate', 'Search Date')}
            value={searchDate ? dayjs(searchDate) : null}
            onChange={(newValue) => {
              setSearchDate(newValue ? newValue.format('YYYY-MM-DD') : '');
              setPage(0);
            }}
            slotProps={{
              textField: {
                size: 'small',
                sx: { minWidth: 200 }
              }
            }}
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="filter-type-label">{t('userActivity.searchBy', 'Search By')}</InputLabel>
            <Select
              labelId="filter-type-label"
              value={filterType}
              label={t('userActivity.searchBy', 'Search By')}
              onChange={handleFilterTypeChange}
            >
              {filterOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.key, option.fallback)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={isTimeFilter ? t('userActivity.timeHHmm', 'Time (HH:mm)') : t('userActivity.searchValue', 'Search Value')}
            type={isTimeFilter ? 'time' : 'text'}
            size="small"
            value={filterValue}
            onChange={(e) => {
              setFilterValue(e.target.value);
              setPage(0);
            }}
            placeholder={filterType === 'All' && !isTimeFilter ? t('userActivity.searchGlobally', 'Search globally...') : ''}
            InputLabelProps={isTimeFilter ? { shrink: true } : undefined}
            sx={{ minWidth: 250, flexGrow: 1 }}
          />

          <Button
            variant="outlined"
            size="medium"
            startIcon={<DownloadIcon />}
            onClick={handleExportClick}
            aria-controls={openExport ? 'export-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={openExport ? 'true' : undefined}
          >
            {t('general.export', 'Export')}
          </Button>

          <Menu id="export-menu" anchorEl={anchorEl} open={openExport} onClose={handleExportClose}>
            <MenuItem onClick={exportToCSV}>{t('userActivity.exportToCsv', 'Export to CSV')}</MenuItem>
            <MenuItem onClick={exportToExcel}>{t('userActivity.exportToExcel', 'Export to Excel')}</MenuItem>
          </Menu>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ flex: 1, overflowX: 'auto', overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1500 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 4 }}>
                <tr>
                  <th style={{ ...headerStyle, ...getStickyStyle(0, 5, '#5166AE'), minWidth: 130 }}>{t('userActivity.ip', 'IP')}</th>
                  <th style={{ ...headerStyle, ...getStickyStyle(130, 5, '#5166AE'), minWidth: 200 }}>{t('general.company', 'Company')}</th>
                  <th style={{ ...headerStyle, ...getStickyStyle(330, 5, '#5166AE'), minWidth: 150 }}>
                    {t('general.username', 'Username')}
                  </th>
                  <th style={headerStyle}>{t('general.role', 'Role')}</th>
                  <th style={{ ...headerStyle, minWidth: 200 }}>{t('userActivity.lastLogin', 'Last Login')}</th>
                  <th style={{ ...headerStyle, minWidth: 200 }}>{t('userActivity.lastAccessEndpoint', 'Last Access Endpoint')}</th>
                  <th style={headerStyle}>{t('userActivity.duration', 'Duration')}</th>
                  <th style={headerStyle}>{t('userActivity.loginCount', 'Login Count')}</th>
                  <th style={headerStyle}>{t('userActivity.accessEndpointCount', 'Access Endpoint Count')}</th>
                  <th style={{ ...headerStyle, minWidth: 1000 }}>{t('userActivity.userAgent', 'User Agent')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={10} style={{ ...cellStyle, textAlign: 'center', padding: '20px' }}>
                      {t('userActivity.noDataFound', 'No data found')}
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row, idx) => {
                    const rowKey = `${row.userId}-${row.ip}-${idx}`;
                    return (
                      <tr key={rowKey}>
                        <td style={{ ...cellStyle, ...getStickyStyle(0, 3, '#fff') }}>{row.ip}</td>
                        <td style={{ ...cellStyle, ...getStickyStyle(130, 3, '#fff') }}>{toText(row.company)}</td>
                        <td style={{ ...cellStyle, ...getStickyStyle(330, 3, '#fff') }}>{row.username}</td>
                        <td style={cellStyle}>{row.role}</td>
                        <td style={cellStyle}>{row.lastLoginAt ? dayjs(row.lastLoginAt).format('DD MMM YYYY HH:mm:ss') : '-'}</td>
                        <td style={cellStyle}>
                          {row.lastAccessEndpointAt ? dayjs(row.lastAccessEndpointAt).format('DD MMM YYYY HH:mm:ss') : '-'}
                        </td>
                        <td style={cellStyle}>{row.durationHms || row.duration}</td>
                        <td style={cellStyle}>{row.loginCount}</td>
                        <td style={cellStyle}>{row.accessEndpointCount}</td>
                        <td style={cellStyle}>
                          {row.userAgents && row.userAgents.length > 0 ? (
                            <List dense sx={{ padding: 0 }}>
                              {row.userAgents.map((ua, i) => (
                                <ListItem key={i} sx={{ alignItems: 'flex-start', px: 0, py: 0.5 }}>
                                  <Box
                                    component="span"
                                    sx={{
                                      display: 'inline-block',
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: 'primary.main',
                                      mt: 1,
                                      mr: 1,
                                      flexShrink: 0
                                    }}
                                  />
                                  <ListItemText
                                    primary={ua}
                                    primaryTypographyProps={{ variant: 'body2', sx: { wordBreak: 'break-word' } }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </Box>
        <Box sx={{ flexShrink: 0, borderTop: '1px solid rgba(224, 224, 224, 1)' }}>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={validPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('userActivity.rowsPerPage', 'Rows per page:')}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('userActivity.of', 'of')} ${count}`}
          />
        </Box>
      </Paper>
    </Box>
  );
}
